import { useStableCallback } from '@main/shared/browser';
import { NAMESPACE } from '@main/shared/utils';
import { OnChangeFn, PaginationState, Updater } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { useTableSettingsNamespace } from '../../utils';

export function useTablePagination(
  tableName: string,
  tableId?: string,
  defaultPageSize = 25,
  initialPageIndex = 0,
  saveDebounceMs = 16,
) {
  const settingsNamespace = useTableSettingsNamespace();

  return useTablePaginationForNamespace(
    tableName,
    settingsNamespace,
    tableId,
    defaultPageSize,
    initialPageIndex,
    saveDebounceMs,
  );
}

export function useTablePaginationForNamespace(
  tableName: string,
  settingsNamespace: string | undefined,
  tableId?: string,
  defaultPageSize = 25,
  initialPageIndex = 0,
  saveDebounceMs = 16,
) {
  const pageSizeKey = useMemo(
    () =>
      NAMESPACE + [tableName, tableId, settingsNamespace, 'page-size'].filter(Boolean).join(':'),
    [tableName, tableId, settingsNamespace],
  );

  const initialPageSize = useMemo(() => {
    const localStorageValue = localStorage.getItem(pageSizeKey);
    const initialString = localStorageValue || `{}`;
    const initialValue =
      (
        JSON.parse(initialString) as {
          pageSize: number;
        }
      ).pageSize ?? defaultPageSize;
    return initialValue;
  }, [defaultPageSize, pageSizeKey]);

  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    const timeout = setTimeout(
      () => localStorage.setItem(pageSizeKey, JSON.stringify({ pageSize })),
      saveDebounceMs,
    );
    return () => clearTimeout(timeout);
  }, [pageSize, pageSizeKey, saveDebounceMs]);

  const [pageIndex, setPageIndex] = useState(initialPageIndex);

  const pagination = useMemo(
    () => ({
      pageSize,
      pageIndex,
    }),
    [pageIndex, pageSize],
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useStableCallback(
    (updater: Updater<PaginationState>) => {
      const newState = typeof updater === 'function' ? updater(pagination) : updater;
      const newPageSize = newState.pageSize;
      if (newPageSize !== undefined) {
        setPageSize(newPageSize);
      }
      const newPageIndex = newState.pageIndex;
      if (newPageIndex !== undefined) {
        setPageIndex(newPageIndex);
      }
    },
  );

  return { pagination, onPaginationChange };
}
